import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Clock, RefreshCw, LogIn, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API, BACKEND_URL } from "@/lib/api";

export default function MaintenancePage({ message, expectedEnd }) {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(null);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    // Fetch logo for branding
    const fetchLogo = async () => {
      try {
        const res = await axios.get(`${API}/branding/logo`);
        if (res.data && res.data.logo_url) {
          setLogo(`${BACKEND_URL}${res.data.logo_url}`);
        }
      } catch (e) {
        console.error("Failed to fetch logo", e);
      }
    };

    fetchLogo();
  }, []);

  useEffect(() => {
    if (!expectedEnd) return;

    const updateTimer = () => {
      const end = new Date(expectedEnd);
      const now = new Date();
      const diff = end - now;

      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft({ days, hours, minutes });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, [expectedEnd]);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Paper Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/p6.png')]" />

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#C19A6B]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#001D24]/5 rounded-full blur-[120px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative z-10 max-w-2xl w-full text-center"
      >
        {/* Logo Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-12"
        >
          {logo ? (
            <img
              src={logo}

              alt="Mokiplaner Logo"
              className="h-20 md:h-24 mx-auto object-contain drop-shadow-sm"
            />
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Sparkles className="w-12 h-12 text-[#C19A6B]" />
              <h2 className="text-2xl font-black text-[#001D24] tracking-tighter">Mokiplaner</h2>
            </div>
          )}
        </motion.div>

        {/* Status Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#001D24]/5 border border-[#001D24]/10 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-[#C19A6B] animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#001D24]/60">Wartungsmodus Aktiv</span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-4xl md:text-6xl font-medium text-[#001D24] mb-6 leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Etwas Großes entsteht gerade.
        </motion.h1>

        {/* Message */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-lg md:text-xl text-[#001D24]/70 mb-12 max-w-lg mx-auto leading-relaxed"
        >
          {message || "Wir nutzen die Zeit für wertvolle Verbesserungen, um dein Erlebnis noch exklusiver zu machen."}
        </motion.p>

        {/* Info Box */}
        {(timeLeft || expectedEnd) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 }}
            className="mb-12 p-8 bg-white/60 backdrop-blur-md border border-[#001D24]/5 rounded-[2.5rem] shadow-xl shadow-[#001D24]/5"
          >
            {timeLeft ? (
              <div className="flex flex-col items-center">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#C19A6B] mb-4">Voraussichtlich online in</p>
                <div className="flex gap-6">
                  {timeLeft.days > 0 && (
                    <>
                      <div className="text-center">
                        <p className="text-4xl font-light text-[#001D24]">{timeLeft.days}</p>
                        <p className="text-[8px] font-black uppercase tracking-widest text-[#001D24]/40 mt-1">Tage</p>
                      </div>
                      <div className="text-4xl font-light text-[#001D24]/20">:</div>
                    </>
                  )}
                  <div className="text-center">
                    <p className="text-4xl font-light text-[#001D24]">{timeLeft.hours}</p>
                    <p className="text-[8px] font-black uppercase tracking-widest text-[#001D24]/40 mt-1">Stunden</p>
                  </div>
                  <div className="text-4xl font-light text-[#001D24]/20">:</div>
                  <div className="text-center">
                    <p className="text-4xl font-light text-[#001D24]">{timeLeft.minutes}</p>
                    <p className="text-[8px] font-black uppercase tracking-widest text-[#001D24]/40 mt-1">Minuten</p>
                  </div>
                </div>
              </div>
            ) : expectedEnd && (
              <div className="flex items-center justify-center gap-3 text-[#001D24]/60">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">Bereit am {new Date(expectedEnd).toLocaleString('de-DE', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            onClick={handleRefresh}
            className="bg-[#001D24] text-white hover:bg-[#001D24]/90 px-10 py-7 rounded-2xl font-black uppercase text-[10px] tracking-[0.3em] transition-all shadow-xl shadow-[#001D24]/20"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button
            onClick={() => navigate("/login")}
            variant="outline"
            className="border-[#001D24]/10 text-[#001D24]/40 hover:text-[#001D24] px-10 py-7 rounded-2xl font-black uppercase text-[10px] tracking-[0.3em] transition-all"
          >
            <LogIn className="w-4 h-4 mr-2" />
            Admin Login
          </Button>
        </motion.div>

        {/* Footer info */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-16 text-[9px] font-black uppercase tracking-[0.5em] text-[#001D24]/20"
        >
          Exklusive Eventplanung &copy; Mokiplaner
        </motion.p>
      </motion.div>
    </div>
  );
}
